import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import {
  formatInstantDateShortTr,
  formatDateTimeTrInAppTz,
  dateStrInAppTz,
} from "./appTime";
import {
  formatPaymentMethod,
  orderLineDisplayAmount,
} from "../approvals/plugins/order/orderDetailUtils";
import { roundMoney } from "./saleDiscountCalc";
import {
  DEFAULT_SALES_TERMS,
  DEFAULT_BANK_INFO,
  resolveOrderSalesTerms,
  resolveOrderBankAccounts,
  resolveOrderDeliveryMeta,
} from "../constants/offerPdfDefaults";
import offerHeaderImg from "../assets/offer/nef_offer_photo.jpg";

pdfMake.vfs = pdfFonts;

const RED = "#c8102e";
const GRAY = "#555555";
const BORDER = "#000000";
/** A4 width (pt) minus left+right pageMargins */
const CONTENT_WIDTH = 539;

const COMPANY = {
  name: "NEF MAKİNE ENDÜSTRİYEL SANAYİ A.Ş.",
  bankName: "Ziraat Bankası / Gaziantep Binevler Şubesi",
  iban: "TR28 0001 0019 5597 6579 7650 01",
};

const STATIC = {
  yetkiliKisi: "-",
  sevkTarihiOffsetDays: 7,
  termin: "STOKTA",
  birim: "ADET",
  teslimatYontemi: "TESLİM",
  opsiyonSuresi: "3 İŞ GÜNÜ",
  ekBilgiNotu: "FİYATLAR, 24 SAAT İÇİN GEÇERLİDİR.",
  kdvRate: 0.2,
};

const DOC_COPY = {
  offer: {
    dateLabel: "TEKLİF TARİHİ",
    noLabel: "TEKLİF NO",
    thirdDateLabel: "SEVK TARİHİ",
    fxNote: "DÖVİZ KURU, TEKLİF ONAY TARİHİ BAZ ALINARAK HESAPLANMAKTADIR.",
    optionLabel: "TEKLİF GEÇERLİLİK SÜRESİ",
    optionValue: STATIC.opsiyonSuresi,
    leftSign: "TEKLIFI VEREN SATIŞ TEMSİLCİSİ",
    filePrefix: "Teklif",
  },
  invoice: {
    dateLabel: "FATURA TARİHİ",
    noLabel: "FATURA NO",
    thirdDateLabel: "VADE TARİHİ",
    fxNote: "DÖVİZ KURU, FATURA TARİHİ BAZ ALINARAK HESAPLANMAKTADIR.",
    optionLabel: "ÖDEME DURUMU",
    optionValue: null, // filled per invoice
    leftSign: "FATURA DÜZENLEYEN",
    filePrefix: "Fatura",
  },
};

let cachedHeaderDataUrl = null;

async function loadOfferHeaderDataUrl() {
  if (cachedHeaderDataUrl) return cachedHeaderDataUrl;
  const res = await fetch(offerHeaderImg);
  const blob = await res.blob();
  cachedHeaderDataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
  return cachedHeaderDataUrl;
}

function moneyUSD(n) {
  const v = Number(n) || 0;
  return `$${v.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function usesTryInPdf(options = {}) {
  return options.displayCurrency === "TRY" && Number(options.exchangeRate) > 0;
}

function createPdfMoneyFormatter(options = {}) {
  const useTry = usesTryInPdf(options);
  const rate = Number(options.exchangeRate) || 1;

  return (usdAmount) => {
    const usd = Number(usdAmount) || 0;
    if (!useTry) return moneyUSD(usd);
    const tryAmount = roundMoney(usd * rate);
    return `${tryAmount.toLocaleString("tr-TR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} ₺`;
  };
}

function pdfCurrencyLabels(options = {}) {
  const useTry = usesTryInPdf(options);
  return {
    unitPriceHeader: useTry ? "B.FİYATI (TL)" : "B.FİYATI (DÖVİZ)",
    subtotalLabel: useTry ? "ARA TOPLAM ₺" : "ARA TOPLAM $",
    grandTotalLabel: useTry ? "TOPLAM TUTAR ₺" : "TOPLAM TUTAR $",
  };
}

function pdfFxNote(data, copy, options = {}) {
  if (!usesTryInPdf(options)) {
    return data.fxNote || copy.fxNote;
  }
  const rate = Number(options.exchangeRate).toFixed(4);
  return `FİYATLAR TCMB EF. SATIŞ KURU (${rate} ₺/USD) İLE TL'YE ÇEVRİLMİŞTİR.`;
}

function addDays(date, days) {
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return new Date();
  d.setDate(d.getDate() + days);
  return d;
}

function paymentLabel(method) {
  const map = {
    cash: "NAKİT",
    credit_card: "KREDİ KARTI",
    bank_transfer: "BANKA TRANSFERİ",
    document: "SENET/BELGE",
  };
  return map[method] || (formatPaymentMethod(method) || "-").toUpperCase();
}

function paymentStatusLabel(status) {
  const map = {
    pending: "BEKLİYOR",
    paid: "ÖDENDİ",
    partial: "KISMI ÖDENDİ",
    overdue: "GECİKMİŞ",
    cancelled: "İPTAL",
    refunded: "İADE",
  };
  return map[status] || String(status || "-").toUpperCase();
}

function labeledCell(label, value, opts = {}) {
  const {
    boldValue = false,
    valueFontSize = 9,
    labelFontSize = 7,
    alignment = "left",
    margin = [4, 3, 4, 3],
  } = opts;
  return {
    margin,
    stack: [
      {
        text: label,
        fontSize: labelFontSize,
        bold: true,
        color: GRAY,
        margin: [0, 0, 0, 2],
      },
      {
        text: value || "-",
        fontSize: valueFontSize,
        bold: boldValue,
        alignment,
      },
    ],
  };
}

function metaLabel(text) {
  return {
    text: `${text} :`,
    bold: true,
    fontSize: 8,
    margin: [4, 5, 2, 5],
  };
}

function metaValue(text) {
  return {
    text: text || "",
    bold: true,
    fontSize: 8,
    margin: [2, 5, 4, 5],
  };
}

/**
 * Shared NEF commercial PDF (offer / invoice).
 * @param {object} data
 * @param {'offer'|'invoice'} data.docType
 */
function buildSalesTermsLine(line) {
  const nakliyePhrase = "Nakliye alıcıya aittir";
  const idx = line.indexOf(nakliyePhrase);
  if (idx === -1) {
    return {
      text: line,
      style: line.startsWith("ÖDEME ŞARTLARI") ? "termsTitle" : "termsLine",
    };
  }

  return {
    text: [
      { text: line.slice(0, idx) },
      {
        text: nakliyePhrase,
        color: RED,
        bold: true,
        decoration: "underline",
      },
      { text: line.slice(idx + nakliyePhrase.length) },
    ],
    style: "termsLine",
  };
}

function buildBankAccountsSection(accounts = []) {
  const list =
    Array.isArray(accounts) && accounts.length > 0
      ? accounts
      : [DEFAULT_BANK_INFO];

  const headerRow = [
    { text: "ALICI ADI SOYADI", bold: true, fontSize: 6, color: "#64748b" },
    { text: "BANKA / ŞUBE", bold: true, fontSize: 6, color: "#64748b" },
    { text: "IBAN", bold: true, fontSize: 6, color: "#64748b" },
  ];

  const dataRows = list.map((account) => [
    {
      text: account.bankRecipientName || COMPANY.name,
      fontSize: 7,
    },
    {
      text: account.bankBranchName || COMPANY.bankName,
      fontSize: 7,
    },
    {
      text: account.bankIban || COMPANY.iban,
      fontSize: 7,
    },
  ]);

  return {
    table: {
      widths: ["*", "*", "*"],
      headerRows: 1,
      body: [
        [
          {
            colSpan: 3,
            margin: [6, 5, 6, 2],
            text: "BANKA HESAP BİLGİLERİ",
            bold: true,
            fontSize: 8,
          },
          {},
          {},
        ],
        headerRow.map((cell) => ({
          ...cell,
          margin: [6, 2, 6, 2],
        })),
        ...dataRows.map((row) =>
          row.map((cell) => ({
            ...cell,
            margin: [6, 2, 6, 3],
          })),
        ),
      ],
    },
    layout: {
      hLineWidth: (i, node) =>
        i === 0 || i === node.table.body.length ? 0.6 : 0.3,
      vLineWidth: () => 0.6,
      hLineColor: () => BORDER,
      vLineColor: () => BORDER,
    },
  };
}

const tableBorderLayout = {
  hLineWidth: () => 0.6,
  vLineWidth: () => 0.6,
  hLineColor: () => BORDER,
  vLineColor: () => BORDER,
};

function buildCommercialPdfFileName(data) {
  const copy = DOC_COPY[data.docType] || DOC_COPY.offer;
  const docNo = data.docNo || "-";
  const safeName = String(docNo)
    .replace(/[^\w\-ğüşıöçĞÜŞİÖÇ]+/gi, "_")
    .slice(0, 40);
  return `${copy.filePrefix}_${safeName}.pdf`;
}

function getPdfBlob(docDefinition) {
  return new Promise((resolve, reject) => {
    try {
      pdfMake.createPdf(docDefinition).getBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error("PDF oluşturulamadı"));
      });
    } catch (err) {
      reject(err);
    }
  });
}

function formatOfferLineTermin(line) {
  if (line?.terminType === "date" && line?.terminDate) {
    const d = new Date(line.terminDate);
    if (!Number.isNaN(d.getTime())) {
      return formatInstantDateShortTr(d);
    }
  }
  if (line?.termin) return String(line.termin);
  return STATIC.termin;
}

async function generateCommercialPDF(data, options = {}) {
  const { action = "download" } = options;
  const formatMoney = createPdfMoneyFormatter(options);
  const currencyLabels = pdfCurrencyLabels(options);
  const copy = DOC_COPY[data.docType] || DOC_COPY.offer;
  const headerImage = await loadOfferHeaderDataUrl();

  const company = data.company || {};
  const docDate = data.docDate ? new Date(data.docDate) : new Date();
  const thirdDate = data.thirdDate ? new Date(data.thirdDate) : null;
  const docNo = data.docNo || "-";

  const lines = (data.lines || []).map((l, i) => ({
    sn: String(i + 1),
    desc: l.desc || "-",
    qty: String(l.qty ?? 0),
    unit: formatMoney(l.unit),
    total: formatMoney(l.total),
    termin: formatOfferLineTermin(l),
  }));

  const araToplam = Number(data.subtotal) || 0;
  const kdv = Math.round(araToplam * STATIC.kdvRate * 100) / 100;
  const genelToplam = Math.round((araToplam + kdv) * 100) / 100;

  const productBody = [
    [
      { text: "S.N", style: "th", alignment: "center" },
      { text: "ÜRÜN KODU / TANIMI", style: "th" },
      { text: "MİKTAR", style: "th", alignment: "center" },
      { text: "BİRİM", style: "th", alignment: "center" },
      { text: currencyLabels.unitPriceHeader, style: "th", alignment: "right" },
      { text: "TUTAR", style: "th", alignment: "right" },
      { text: "TERMİN", style: "th", alignment: "center" },
    ],
    ...lines.map((l) => [
      { text: l.sn, style: "td", alignment: "center" },
      { text: l.desc, style: "td" },
      { text: l.qty, style: "td", alignment: "center" },
      { text: STATIC.birim, style: "td", alignment: "center" },
      { text: l.unit, style: "td", alignment: "right" },
      { text: l.total, style: "td", alignment: "right" },
      { text: l.termin, style: "td", alignment: "center" },
    ]),
  ];

  const optionValue =
    data.opsiyonSuresi || copy.optionValue || STATIC.opsiyonSuresi;

  const salesTermsLines = String(data.salesTerms || DEFAULT_SALES_TERMS)
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  const salesTermsStack = salesTermsLines.map((line) => {
    if (
      line.toUpperCase() === "SATIŞ KOŞULLARI" ||
      line.startsWith("ÖDEME ŞARTLARI")
    ) {
      return { text: line, style: "termsTitle" };
    }
    return buildSalesTermsLine(line);
  });

  const docDefinition = {
    pageSize: "A4",
    pageMargins: [28, 24, 28, 24],
    defaultStyle: {
      font: "Roboto",
      fontSize: 9,
      color: "#000000",
    },
    styles: {
      th: {
        fontSize: 7,
        bold: true,
        fillColor: "#f5f5f5",
        margin: [2, 4, 2, 4],
      },
      td: {
        fontSize: 8,
        margin: [2, 5, 2, 5],
      },
      redNote: {
        fontSize: 7,
        italics: true,
        color: RED,
      },
      termsTitle: {
        fontSize: 8,
        bold: true,
        margin: [0, 0, 0, 4],
      },
      termsLine: {
        fontSize: 7,
        margin: [0, 0, 0, 1.5],
      },
    },
    content: [
      {
        table: {
          widths: ["*"],
          body: [
            [
              {
                image: headerImage,
                width: CONTENT_WIDTH - 2,
                margin: [0, 0, 0, 0],
              },
            ],
          ],
        },
        layout: {
          hLineWidth: (i, node) => (i === node.table.body.length ? 0 : 0.7),
          vLineWidth: () => 0.7,
          hLineColor: () => BORDER,
          vLineColor: () => BORDER,
          paddingLeft: () => 1,
          paddingRight: () => 1,
          paddingTop: () => 1,
          paddingBottom: () => 1,
        },
        margin: [0, 0, 0, 0],
      },

      {
        table: {
          widths: ["28%", "34%", "18%", "20%"],
          body: [
            [
              metaLabel("MÜŞTERİ ÜNVANI"),
              metaValue(company.name || ""),
              metaLabel(copy.dateLabel),
              metaValue(formatInstantDateShortTr(docDate)),
            ],
            [
              metaLabel("YETKİLİ KİŞİ"),
              metaValue(data.yetkiliKisi || STATIC.yetkiliKisi),
              metaLabel(copy.noLabel),
              metaValue(docNo),
            ],
            [
              metaLabel("TEBLİGAT ADRESİ"),
              metaValue(company.address || ""),
              metaLabel(copy.thirdDateLabel),
              metaValue(thirdDate ? formatInstantDateShortTr(thirdDate) : ""),
            ],
            [
              metaLabel("VERGİ DAİRESİ / NO"),
              {
                ...metaValue(
                  [company.taxOffice, company.taxNumber]
                    .filter(Boolean)
                    .join(" / ") || "",
                ),
                colSpan: 3,
              },
              {},
              {},
            ],
            [
              metaLabel("MAIL ADRESİ"),
              {
                ...metaValue(company.email || ""),
                colSpan: 3,
              },
              {},
              {},
            ],
            [
              metaLabel("SEVK ADRESİ"),
              {
                ...metaValue(company.address || ""),
                colSpan: 3,
              },
              {},
              {},
            ],
          ],
        },
        layout: {
          hLineWidth: () => 0.7,
          vLineWidth: () => 0.7,
          hLineColor: () => BORDER,
          vLineColor: () => BORDER,
          paddingLeft: () => 0,
          paddingRight: () => 0,
          paddingTop: () => 0,
          paddingBottom: () => 0,
        },
      },

      {
        table: {
          headerRows: 1,
          widths: [22, "*", 36, 32, 70, 58, 75],
          body: productBody,
        },
        layout: {
          hLineWidth: () => 0.6,
          vLineWidth: () => 0.6,
          hLineColor: () => BORDER,
          vLineColor: () => BORDER,
        },
        margin: [0, 0, 0, 0],
      },

      {
        columns: [
          {
            width: "*",
            text: pdfFxNote(data, copy, options),
            style: "redNote",
            margin: [2, 8, 8, 0],
          },
          {
            width: 180,
            table: {
              widths: ["*", 70],
              body: [
                [
                  {
                    text: currencyLabels.subtotalLabel,
                    bold: true,
                    fontSize: 8,
                  },
                  {
                    text: formatMoney(araToplam),
                    alignment: "right",
                    fontSize: 8,
                  },
                ],
                [
                  { text: "KDV (%20)", bold: true, fontSize: 8 },
                  { text: formatMoney(kdv), alignment: "right", fontSize: 8 },
                ],
                [
                  {
                    text: currencyLabels.grandTotalLabel,
                    bold: true,
                    fontSize: 9,
                    color: RED,
                  },
                  {
                    text: formatMoney(genelToplam),
                    alignment: "right",
                    bold: true,
                    fontSize: 9,
                    color: RED,
                  },
                ],
              ],
            },
            layout: {
              hLineWidth: () => 0.6,
              vLineWidth: () => 0.6,
              hLineColor: () => BORDER,
              vLineColor: () => BORDER,
              paddingLeft: () => 4,
              paddingRight: () => 4,
              paddingTop: () => 4,
              paddingBottom: () => 4,
            },
          },
        ],
        margin: [0, 12, 0, 12],
      },

      {
        table: {
          widths: ["22%", "34%", "22%", "22%"],
          body: [
            [
              labeledCell("ÖDEME ŞEKLİ", paymentLabel(data.paymentMethod), {
                boldValue: true,
                valueFontSize: 8,
              }),
              labeledCell(
                "EK BİLGİ NOTU",
                data.ekBilgiNotu || STATIC.ekBilgiNotu,
                {
                  boldValue: true,
                  valueFontSize: 7,
                },
              ),
              labeledCell(
                "TESLİMAT YÖNTEMİ",
                data.teslimatYontemi || STATIC.teslimatYontemi,
                {
                  boldValue: true,
                  valueFontSize: 8,
                },
              ),
              labeledCell(copy.optionLabel, optionValue, {
                boldValue: true,
                valueFontSize: 8,
              }),
            ],
          ],
        },
        layout: {
          hLineWidth: () => 0.6,
          vLineWidth: () => 0.6,
          hLineColor: () => BORDER,
          vLineColor: () => BORDER,
        },
        margin: [0, 0, 0, 0],
      },

      {
        table: {
          widths: ["*"],
          body: [
            [
              {
                margin: [6, 6, 6, 6],
                stack: salesTermsStack,
              },
            ],
          ],
        },
        layout: {
          hLineWidth: () => 0.6,
          vLineWidth: () => 0.6,
          hLineColor: () => BORDER,
          vLineColor: () => BORDER,
        },
      },

      {
        table: {
          widths: ["50%", "50%"],
          body: [
            [
              {
                stack: [
                  {
                    text: copy.leftSign,
                    bold: true,
                    fontSize: 8,
                    margin: [4, 4, 4, 0],
                  },
                  {
                    text: "ADI SOYADI / İMZA",
                    fontSize: 8,
                    margin: [4, 6, 4, 0],
                  },
                  {
                    text: data.leftSignName || " ",
                    fontSize: 9,
                    bold: true,
                    margin: [4, 10, 4, 0],
                  },
                  { text: " ", margin: [0, 24, 0, 8] },
                ],
                minHeight: 70,
              },
              {
                stack: [
                  {
                    text: "MÜŞTERİ YETKİLİ ONAYI",
                    bold: true,
                    fontSize: 8,
                    margin: [4, 4, 4, 0],
                  },
                  {
                    text: "ADI SOYADI / İMZA",
                    fontSize: 8,
                    margin: [4, 6, 4, 0],
                  },
                  { text: " ", margin: [0, 36, 0, 8] },
                ],
                minHeight: 70,
              },
            ],
          ],
        },
        layout: {
          hLineWidth: () => 0.6,
          vLineWidth: () => 0.6,
          hLineColor: () => BORDER,
          vLineColor: () => BORDER,
        },
        margin: [0, 0, 0, 0],
      },

      buildBankAccountsSection(data.bankAccounts),
    ],
  };

  const fileName = buildCommercialPdfFileName(data);
  if (action === "blob") {
    const blob = await getPdfBlob(docDefinition);
    return { blob, fileName };
  }
  pdfMake.createPdf(docDefinition).download(fileName);
  return { fileName };
}

function buildOfferPdfPayload(order) {
  const company = order.companyId || {};
  const offerDate = order.orderDate ? new Date(order.orderDate) : new Date();
  const models = order.models || [];
  const salesTerms = resolveOrderSalesTerms(order);
  const bankAccounts = resolveOrderBankAccounts(order);
  const deliveryMeta = resolveOrderDeliveryMeta(order);

  const lines = models.map((m) => {
    const code = m.modelId?.code || "";
    const name = m.modelId?.name || "-";
    const qty = Number(m.count) || 0;
    const lineTotal = orderLineDisplayAmount(m, order);
    const unitPrice =
      qty > 0 ? roundMoney(lineTotal / qty) : Number(m.modelId?.price) || 0;
    return {
      desc: code ? `${code} / ${name}` : name,
      qty,
      unit: unitPrice,
      total: lineTotal,
      terminType: m.terminType === "date" ? "date" : "stock",
      terminDate: m.terminDate || null,
    };
  });

  const subtotal =
    Number(order.totalAmount) ||
    lines.reduce((s, l) => s + (Number(l.total) || 0), 0);

  const leftSignName =
    order?.createdBy?.name ||
    order?.logs?.[0]?.user?.name ||
    order?.logs?.[0]?.createdBy?.name ||
    "";

  return {
    docType: "offer",
    company: {
      name: company.name,
      address: company.address,
      email: company.email,
      taxOffice: company.taxOffice,
      taxNumber: company.taxNumber,
    },
    yetkiliKisi: order.yetkiliKisi || company.yetkiliKisi || STATIC.yetkiliKisi,
    leftSignName,
    docDate: offerDate,
    docNo:
      order.orderName || `NEF-${order._id?.toString?.().slice(-8) || "0000"}`,
    thirdDate: order.terminTarihi
      ? new Date(order.terminTarihi)
      : addDays(offerDate, STATIC.sevkTarihiOffsetDays),
    lines,
    subtotal,
    paymentMethod: order.paymentMethod,
    salesTerms,
    bankAccounts,
    ...deliveryMeta,
  };
}

/** Price offer PDF from an order. action: "download" (default) | "blob" */
export async function generateOfferPDF(order, options = {}) {
  if (!order) return;
  const orderRateNum =
    order.exchangeRate !== undefined && order.exchangeRate !== null
      ? Number(String(order.exchangeRate).replace(",", "."))
      : undefined;
  const orderRate =
    orderRateNum !== undefined && !isNaN(orderRateNum) && orderRateNum > 0
      ? orderRateNum
      : undefined;
  const resolvedRate =
    options.exchangeRate !== undefined && options.exchangeRate !== null
      ? options.exchangeRate
      : orderRate;

  return generateCommercialPDF(buildOfferPdfPayload(order), {
    ...options,
    ...(resolvedRate !== undefined ? { exchangeRate: resolvedRate } : {}),
  });
}

/** Device sale products list PDF (Device Sales page). action: "download" (default) | "blob" */
export async function generateSaleProductsPDF(sale, options = {}) {
  if (!sale) return;

  const headerImage = await loadOfferHeaderDataUrl();
  const models = Array.isArray(sale.models) ? sale.models : [];

  const productRows = models.map((modelItem) => {
    const quantity = Number(modelItem.count) || 0;
    const delivered = Number(modelItem.deliveredCount) || 0;
    const isComplete = quantity > 0 && delivered >= quantity;

    return [
      { text: modelItem.modelId?.name || "Bilinmeyen Model", style: "td" },
      {
        text: modelItem.modelId?.code || "-",
        style: "td",
        alignment: "center",
      },
      { text: String(quantity), style: "td", alignment: "center" },
      {
        text: String(delivered),
        style: "td",
        alignment: "center",
        bold: isComplete,
        color: isComplete ? "#008000" : "#000000",
      },
    ];
  });

  const productBody = [
    [
      { text: "ÜRÜN ADI", style: "th" },
      { text: "ÜRÜN KODU", style: "th", alignment: "center" },
      { text: "ADET", style: "th", alignment: "center" },
      { text: "TESLİM EDİLEN", style: "th", alignment: "center" },
    ],
    ...(productRows.length > 0
      ? productRows
      : [
          [
            {
              text: "Ürün bulunamadı",
              style: "td",
              colSpan: 4,
              alignment: "center",
              italics: true,
              color: GRAY,
            },
            {},
            {},
            {},
          ],
        ]),
  ];

  const totalDevices = models.reduce(
    (sum, modelItem) => sum + (Number(modelItem.count) || 0),
    0,
  );
  const totalDelivered = models.reduce(
    (sum, modelItem) => sum + (Number(modelItem.deliveredCount) || 0),
    0,
  );
  const pending = Math.max(0, totalDevices - totalDelivered);

  const infoRows = [];
  if (sale.saleName) infoRows.push(["Sipariş Adı", sale.saleName]);
  if (sale.companyId?.name) infoRows.push(["Şirket", sale.companyId.name]);
  if (sale.companyId?.code) infoRows.push(["Kod", sale.companyId.code]);
  if (sale.saleDate) {
    infoRows.push(["Tarih", formatInstantDateShortTr(sale.saleDate)]);
  }

  const docDefinition = {
    pageSize: "A4",
    pageMargins: [28, 24, 28, 36],
    defaultStyle: {
      font: "Roboto",
      fontSize: 9,
      color: "#000000",
    },
    styles: {
      th: {
        fontSize: 8,
        bold: true,
        fillColor: "#f5f5f5",
        margin: [4, 5, 4, 5],
      },
      td: {
        fontSize: 8,
        margin: [4, 5, 4, 5],
      },
      docTitle: {
        fontSize: 13,
        bold: true,
        alignment: "center",
        margin: [0, 8, 0, 2],
      },
      docSubtitle: {
        fontSize: 9,
        alignment: "center",
        color: GRAY,
        margin: [0, 0, 0, 10],
      },
    },
    footer: (currentPage, pageCount) => ({
      columns: [
        {
          text: `Oluşturulma: ${formatDateTimeTrInAppTz(new Date())}`,
          fontSize: 7,
          color: GRAY,
          margin: [28, 0, 0, 0],
        },
        {
          text: `Sayfa ${currentPage} / ${pageCount}`,
          alignment: "right",
          fontSize: 7,
          color: GRAY,
          margin: [0, 0, 28, 0],
        },
      ],
    }),
    content: [
      {
        image: headerImage,
        width: CONTENT_WIDTH,
        margin: [0, 0, 0, 4],
      },
      { text: "SATIŞTAKİ ÜRÜNLER LİSTESİ", style: "docTitle" },
      { text: sale.saleName || "", style: "docSubtitle" },
      {
        table: {
          widths: ["28%", "72%"],
          body: [
            [
              {
                text: "SATIŞ BİLGİLERİ",
                bold: true,
                fontSize: 9,
                fillColor: "#f5f5f5",
                colSpan: 2,
                margin: [6, 5, 6, 5],
              },
              {},
            ],
            ...infoRows.map(([label, value]) => [
              {
                text: label,
                bold: true,
                fontSize: 8,
                color: GRAY,
                margin: [6, 4, 4, 4],
              },
              { text: value || "-", fontSize: 8, margin: [4, 4, 6, 4] },
            ]),
          ],
        },
        layout: tableBorderLayout,
        margin: [0, 0, 0, 10],
      },
      {
        table: {
          headerRows: 1,
          widths: ["*", 90, 55, 75],
          body: productBody,
        },
        layout: tableBorderLayout,
      },
      {
        table: {
          widths: ["*", "*", "*"],
          body: [
            [
              {
                text: `Toplam Ürün: ${totalDevices}`,
                bold: true,
                fontSize: 8,
                alignment: "center",
                margin: [4, 6, 4, 6],
              },
              {
                text: `Teslim Edilen: ${totalDelivered}`,
                bold: true,
                fontSize: 8,
                color: "#008000",
                alignment: "center",
                margin: [4, 6, 4, 6],
              },
              {
                text: `Bekleyen: ${pending}`,
                bold: true,
                fontSize: 8,
                color: pending > 0 ? "#ff8c00" : GRAY,
                alignment: "center",
                margin: [4, 6, 4, 6],
              },
            ],
          ],
        },
        layout: tableBorderLayout,
        margin: [0, 10, 0, 0],
      },
      {
        text: "Bu belge Nef Makine Rovion MES sistemi tarafından elektronik ortamda oluşturulmuştur.",
        fontSize: 7,
        color: GRAY,
        alignment: "center",
        margin: [0, 12, 0, 0],
      },
    ],
  };

  const safeName = String(sale.saleName || sale._id?.slice?.(-8) || "satis")
    .replace(/[^\w\-ğüşıöçĞÜŞİÖÇ]+/gi, "_")
    .slice(0, 40);
  const fileName = `Satis_Urunler_${safeName}_${dateStrInAppTz(new Date())}.pdf`;
  if (options.action === "blob") {
    const blob = await getPdfBlob(docDefinition);
    return { blob, fileName };
  }
  pdfMake.createPdf(docDefinition).download(fileName);
  return { fileName };
}

function groupDeliveryDevicesByModel(devices = []) {
  const map = new Map();
  for (const device of devices) {
    const name =
      device?.modelId?.name || device?.model?.name || "Bilinmeyen Model";
    const code = device?.modelId?.code || device?.model?.code || "-";
    const key = `${code}::${name}`;
    if (!map.has(key)) {
      map.set(key, { name, code, count: 0 });
    }
    map.get(key).count += 1;
  }
  return [...map.values()];
}

function buildDeliverySignatureSection(deliveredByName, receiverName) {
  return {
    table: {
      widths: ["50%", "50%"],
      body: [
        [
          {
            stack: [
              {
                text: "TESLİM EDEN KİŞİ",
                bold: true,
                fontSize: 8,
                margin: [4, 4, 4, 0],
              },
              {
                text: "ADI SOYADI / İMZA",
                fontSize: 8,
                margin: [4, 6, 4, 0],
              },
              {
                text: deliveredByName || " ",
                fontSize: 9,
                bold: true,
                margin: [4, 10, 4, 0],
              },
              { text: " ", margin: [0, 28, 0, 8] },
            ],
            minHeight: 80,
          },
          {
            stack: [
              {
                text: "TESLİM ALAN",
                bold: true,
                fontSize: 8,
                margin: [4, 4, 4, 0],
              },
              {
                text: "ADI SOYADI / İMZA",
                fontSize: 8,
                margin: [4, 6, 4, 0],
              },
              {
                text: receiverName || " ",
                fontSize: 9,
                bold: true,
                margin: [4, 10, 4, 0],
              },
              { text: " ", margin: [0, 28, 0, 8] },
            ],
            minHeight: 80,
          },
        ],
      ],
    },
    layout: tableBorderLayout,
    margin: [0, 14, 0, 0],
  };
}

/** Delivery products list PDF (Deliveries page). action: "download" (default) | "blob" */
export async function generateDeliveryProductsPDF(delivery, options = {}) {
  if (!delivery) return;

  const headerImage = await loadOfferHeaderDataUrl();
  const modelGroups = groupDeliveryDevicesByModel(delivery.devices || []);
  const totalDelivered = (delivery.devices || []).length;

  const productRows = modelGroups.map((model) => [
    { text: model.name, style: "td" },
    { text: model.code, style: "td", alignment: "center" },
    { text: String(model.count), style: "td", alignment: "center", bold: true },
  ]);

  const productBody = [
    [
      { text: "ÜRÜN ADI", style: "th" },
      { text: "ÜRÜN KODU", style: "th", alignment: "center" },
      { text: "TESLİM EDİLEN", style: "th", alignment: "center" },
    ],
    ...(productRows.length > 0
      ? productRows
      : [
          [
            {
              text: "Ürün bulunamadı",
              style: "td",
              colSpan: 3,
              alignment: "center",
              italics: true,
              color: GRAY,
            },
            {},
            {},
          ],
        ]),
  ];

  const deliveryWhen = delivery.deliveryDate
    ? `${formatInstantDateShortTr(delivery.deliveryDate)}${
        delivery.deliveryTime ? ` ${delivery.deliveryTime}` : ""
      }`.trim()
    : delivery.transferDate
      ? formatInstantDateShortTr(delivery.transferDate)
      : "-";

  const deliveredByName =
    delivery.createdBy?.name ||
    delivery.createdBy?.email ||
    delivery.updatedBy?.name ||
    "-";

  const infoRows = [
    ["Sevkiyat Adı", delivery.deliveryName || "-"],
    ["Sipariş", delivery.saleId?.saleName || "-"],
    ["Firma", delivery.saleId?.companyId?.name || "-"],
    ["Sevkiyat Tarihi", deliveryWhen],
    ["Araç Plakası", delivery.vehiclePlate || "-"],
    ["Teslim Alan", delivery.driverName || "-"],
    ["Teslim Eden Kişi", deliveredByName],
  ];

  if (delivery.notes) {
    infoRows.push(["Notlar", delivery.notes]);
  }

  const docDefinition = {
    pageSize: "A4",
    pageMargins: [28, 24, 28, 36],
    defaultStyle: {
      font: "Roboto",
      fontSize: 9,
      color: "#000000",
    },
    styles: {
      th: {
        fontSize: 8,
        bold: true,
        fillColor: "#f5f5f5",
        margin: [4, 5, 4, 5],
      },
      td: {
        fontSize: 8,
        margin: [4, 5, 4, 5],
      },
      docTitle: {
        fontSize: 13,
        bold: true,
        alignment: "center",
        margin: [0, 8, 0, 2],
      },
      docSubtitle: {
        fontSize: 9,
        alignment: "center",
        color: GRAY,
        margin: [0, 0, 0, 10],
      },
    },
    footer: (currentPage, pageCount) => ({
      columns: [
        {
          text: `Oluşturulma: ${formatDateTimeTrInAppTz(new Date())}`,
          fontSize: 7,
          color: GRAY,
          margin: [28, 0, 0, 0],
        },
        {
          text: `Sayfa ${currentPage} / ${pageCount}`,
          alignment: "right",
          fontSize: 7,
          color: GRAY,
          margin: [0, 0, 28, 0],
        },
      ],
    }),
    content: [
      {
        image: headerImage,
        width: CONTENT_WIDTH,
        margin: [0, 0, 0, 4],
      },
      { text: "SEVKİYAT TESLİMAT LİSTESİ", style: "docTitle" },
      {
        text: delivery.deliveryName || delivery.saleId?.saleName || "",
        style: "docSubtitle",
      },
      {
        table: {
          widths: ["28%", "72%"],
          body: [
            [
              {
                text: "SEVKİYAT BİLGİLERİ",
                bold: true,
                fontSize: 9,
                fillColor: "#f5f5f5",
                colSpan: 2,
                margin: [6, 5, 6, 5],
              },
              {},
            ],
            ...infoRows.map(([label, value]) => [
              {
                text: label,
                bold: true,
                fontSize: 8,
                color: GRAY,
                margin: [6, 4, 4, 4],
              },
              { text: value || "-", fontSize: 8, margin: [4, 4, 6, 4] },
            ]),
          ],
        },
        layout: tableBorderLayout,
        margin: [0, 0, 0, 10],
      },
      {
        table: {
          headerRows: 1,
          widths: ["*", 90, 75],
          body: productBody,
        },
        layout: tableBorderLayout,
      },
      {
        table: {
          widths: ["*"],
          body: [
            [
              {
                text: `Toplam Teslim Edilen: ${totalDelivered}`,
                bold: true,
                fontSize: 8,
                alignment: "center",
                color: "#008000",
                margin: [4, 8, 4, 8],
              },
            ],
          ],
        },
        layout: tableBorderLayout,
        margin: [0, 10, 0, 0],
      },
      buildDeliverySignatureSection(deliveredByName, delivery.driverName || ""),
      {
        text: "Bu belge Nef Makine Rovion MES sistemi tarafından elektronik ortamda oluşturulmuştur.",
        fontSize: 7,
        color: GRAY,
        alignment: "center",
        margin: [0, 12, 0, 0],
      },
    ],
  };

  const safeName = String(
    delivery.deliveryName ||
      delivery.saleId?.saleName ||
      delivery._id?.slice?.(-8) ||
      "sevkiyat",
  )
    .replace(/[^\w\-ğüşıöçĞÜŞİÖÇ]+/gi, "_")
    .slice(0, 40);
  const fileName = `Sevkiyat_Urunler_${safeName}_${dateStrInAppTz(new Date())}.pdf`;
  if (options.action === "blob") {
    const blob = await getPdfBlob(docDefinition);
    return { blob, fileName };
  }
  pdfMake.createPdf(docDefinition).download(fileName);
  return { fileName };
}

/**
 * Invoice PDF from sale invoice payload (same layout as offer).
 * Accepts DeviceSales invoiceData shape (+ optional company object on sale).
 */
export async function generateInvoicePDF(
  invoiceData,
  sale = null,
  options = {},
) {
  if (!invoiceData) return;

  const companyFromSale =
    sale?.companyId && typeof sale.companyId === "object" ? sale.companyId : {};

  const company = {
    name: invoiceData.companyName || companyFromSale.name || "",
    address: companyFromSale.address || invoiceData.companyAddress || "",
    email: companyFromSale.email || invoiceData.companyEmail || "",
    taxOffice: companyFromSale.taxOffice || invoiceData.taxOffice || "",
    taxNumber: companyFromSale.taxNumber || invoiceData.taxNumber || "",
  };

  const items = invoiceData.items || [];
  const lines = items.map((item) => {
    const code = item.modelCode || "";
    const name = item.modelName || "-";
    return {
      desc: code ? `${code} / ${name}` : name,
      qty: Number(item.quantity) || 0,
      unit: Number(item.unitPrice) || 0,
      total:
        Number(item.totalPrice) ||
        (Number(item.unitPrice) || 0) * (Number(item.quantity) || 0),
    };
  });

  const subtotal =
    Number(invoiceData.totalAmount) ||
    lines.reduce((s, l) => s + (Number(l.total) || 0), 0);

  return generateCommercialPDF(
    {
      docType: "invoice",
      company,
      yetkiliKisi:
        sale?.createdBy?.name ||
        invoiceData.createdByName ||
        STATIC.yetkiliKisi,
      leftSignName: sale?.createdBy?.name || invoiceData.createdByName || "",
      docDate: invoiceData.invoiceDate || new Date(),
      docNo: invoiceData.invoiceNumber || "INV-0000",
      thirdDate: invoiceData.dueDate || null,
      lines,
      subtotal,
      paymentMethod: invoiceData.paymentMethod,
      optionValue: paymentStatusLabel(invoiceData.paymentStatus),
      notes: invoiceData.notes || invoiceData.saleName || "",
    },
    options,
  );
}

export default generateOfferPDF;
